import { graphql } from 'react-relay';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { getStatusCode } from '@confluence/error-boundary/entry-points/isStatusCodeError.ts';

import type {
	useOrgDefaultClassificationLevelIdOrgDefaultClassificationLevelIdQuery$data,
	useOrgDefaultClassificationLevelIdOrgDefaultClassificationLevelIdQuery$variables,
} from './__generated__/useOrgDefaultClassificationLevelIdOrgDefaultClassificationLevelIdQuery.graphql.ts';

type UseOrgDefaultClassificationLevelIdProps = {
	skip?: boolean;
};

type UseOrgDefaultClassificationLevelIdType = {
	orgDefaultClassificationLevelId: string | null;
	orgDefaultClasificationLoading: boolean;
	orgDefaultClassificationError?: ApolloErrorInterop;
};

const shouldMarkErrorAsHandled = (error?: ApolloErrorInterop) => {
	if (!error) return true;
	const statusCode = getStatusCode(error?.graphQLErrors?.[0]);
	if (!statusCode) return true;
	return !!(parseInt(statusCode) >= 400 && parseInt(statusCode) < 500);
};

export const useOrgDefaultClassificationLevelId = ({
	skip,
}: UseOrgDefaultClassificationLevelIdProps): UseOrgDefaultClassificationLevelIdType => {
	const { data, loading, error } = useQueryInterop<
		useOrgDefaultClassificationLevelIdOrgDefaultClassificationLevelIdQuery$data,
		useOrgDefaultClassificationLevelIdOrgDefaultClassificationLevelIdQuery$variables
	>(
		graphql`
			query useOrgDefaultClassificationLevelIdOrgDefaultClassificationLevelIdQuery {
				orgDefaultClassificationLevelId
			}
		`,
		{ skip },
	);

	if (shouldMarkErrorAsHandled(error)) {
		markErrorAsHandled(error);
	}

	const orgDefaultClassificationLevelId = data?.orgDefaultClassificationLevelId ?? null;

	return {
		orgDefaultClassificationLevelId,
		orgDefaultClasificationLoading: loading,
		orgDefaultClassificationError: !shouldMarkErrorAsHandled(error) ? error : undefined,
	};
};
